import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { ACTION } from '@atlaskit/editor-common/analytics';
import { ACTION_SUBJECT_ID, EVENT_TYPE, ACTION_SUBJECT } from '@atlaskit/editor-common/analytics';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics';

import type { EditorOfflineStoragePlugin } from './editorOfflineStoragePluginType';
import type { Status } from './pm-plugins/resync-online';

type BaseAnalyticsEventProps = {
	api: PublicPluginAPI<[EditorOfflineStoragePlugin, AnalyticsPlugin]> | undefined;
};

type ResyncOnlineEventProps = BaseAnalyticsEventProps & {
	action: ACTION.OFFLINE_STORAGE_RESYNC_ONLINE;
	resyncStatus: Status;
	isActiveTab: boolean;
};

type BroadcastChannelUpdateEventProps = BaseAnalyticsEventProps & {
	action: ACTION.OFFLINE_STORAGE_BROADCAST_CHANNEL_FIRST_UPDATE;
};

type TooManyRetriesErrorEventProps = BaseAnalyticsEventProps & {
	action: ACTION.OFFLINE_STORAGE_TOO_MANY_RETRIES_ERROR;
	errorMessage: string;
	errorInfo: string;
	retrySource: string;
	maxRetries: number;
};

type RestoreStepsInitiatedEventProps = BaseAnalyticsEventProps & {
	action: ACTION.OFFLINE_STORAGE_RESTORE_STEPS_INITIATED;
	retryCount: number;
};

type FailedStepsErrorEventProps = BaseAnalyticsEventProps & {
	action: ACTION.OFFLINE_STORAGE_FAILED_STEPS;
	errorInfo: string;
	stepsCount: number;
};

type StepsDatabaseErrorEventProps = BaseAnalyticsEventProps & {
	action: ACTION.OFFLINE_STORAGE_STEPS_DATABASE_ERROR;
	errorMessage: string;
	errorSource: string;
	errorCause: {
		contentIdPresent: boolean;
		stepsDatabaseInitAttempted: boolean;
		isSSR: boolean;
		isIndexedDBAvailable: boolean | undefined;
	};
};

type FailedStoreAction = 'OFFLINE_EDITING_FAILED_STORE_RECOVERY_VERSION';
type FailedStoreOfflineVersion = BaseAnalyticsEventProps & {
	action: FailedStoreAction;
	errorMessage: string;
	manualRetryCount?: number;
};

type TrackStoreAction = 'OFFLINE_EDITING_SUCCESS_STORE_RECOVERY_VERSION';
type TrackStoreOfflineVersion = BaseAnalyticsEventProps & {
	action: TrackStoreAction;
	success: boolean | undefined;
	manualRetryCount?: number;
};

type VersionInsertionErrorFlagDismissedAction =
	'OFFLINE_EDITING_VERSION_INSERTION_ERROR_FLAG_DISMISSED';
type VersionInsertionErrorFlagDismissedEventProps = BaseAnalyticsEventProps & {
	action: VersionInsertionErrorFlagDismissedAction;
	manualRetryCount: number;
};

type DataExpiryCleanupAction = 'OFFLINE_EDITING_DATA_EXPIRY_CLEANUP';
type DataExpiryCleanupEventProps = BaseAnalyticsEventProps & {
	action: DataExpiryCleanupAction;
	contentId: string;
	cleanupType: 'database_cleared' | 'database_deleted';
	daysExpired: number;
	stepsCount: number;
	hasBackup: boolean;
	isBackgroundCleanup?: boolean;
	totalOfflineDatabases?: number;
};

type BackgroundCleanupErrorAction = 'OFFLINE_EDITING_BACKGROUND_CLEANUP_ERROR';
type BackgroundCleanupErrorEventProps = BaseAnalyticsEventProps & {
	action: BackgroundCleanupErrorAction;
	errorMessage: string;
	errorSource: 'database_check' | 'cleanup_init';
	contentId?: string;
	totalOfflineDatabases?: number;
};

/**
 * Forces a value to a specific type using TypeScript's 'as' keyword.
 * Usage: const forcedValue = value as DesiredType;
 */
type FireAnalyticsEventProps =
	| TooManyRetriesErrorEventProps
	| StepsDatabaseErrorEventProps
	| ResyncOnlineEventProps
	| BroadcastChannelUpdateEventProps
	| FailedStepsErrorEventProps
	| FailedStoreOfflineVersion
	| TrackStoreOfflineVersion
	| VersionInsertionErrorFlagDismissedEventProps
	| DataExpiryCleanupEventProps
	| RestoreStepsInitiatedEventProps
	| BackgroundCleanupErrorEventProps;

const isTrackEvent = (props: FireAnalyticsEventProps) => {
	return props.action === 'OFFLINE_EDITING_SUCCESS_STORE_RECOVERY_VERSION';
};

type BaseEventProps = {
	actionSubject: ACTION_SUBJECT.OFFLINE_EDITING;
	actionSubjectId: ACTION_SUBJECT_ID.OFFLINE_EDITING;
	eventType: EVENT_TYPE.TRACK | EVENT_TYPE.OPERATIONAL;
	attributes?: {
		[key: string]: unknown;
	};
};

export const fireOfflineStorageAnalyticsEvent = (props: FireAnalyticsEventProps) => {
	const { api, action, ...otherAttributes } = props;
	if (!api || !api.analytics) {
		return;
	}

	// Get additional context from shared state
	const sharedPluginState = api?.editorOfflineStorage?.sharedState.currentState();
	const { version, steps, status, disabled } = sharedPluginState ?? {};

	api.analytics.actions?.fireAnalyticsEvent?.<
		Omit<FireAnalyticsEventProps, 'api'> & BaseEventProps,
		'customEventType'
	>({
		eventType: isTrackEvent(props) ? EVENT_TYPE.TRACK : EVENT_TYPE.OPERATIONAL,
		action,
		actionSubject: ACTION_SUBJECT.OFFLINE_EDITING,
		actionSubjectId: ACTION_SUBJECT_ID.OFFLINE_EDITING,
		attributes: {
			// Additonal context.
			version,
			stepsCount: steps?.length,
			status,
			disabled,
			// Action specific attributes.
			...otherAttributes,
		},
	});
};

export const parseErrorMessage = (error: Error | unknown) => {
	if (!error) {
		return 'Unknown error';
	}
	if (error instanceof Error) {
		return `${error.name}: ${error.message}`;
	}
	return String(error);
};

export const parseErrorMessages = (errors: Error[]) => {
	return errors.map((e) => parseErrorMessage(e)).join(';');
};
